import { z } from 'zod';

/** Варианты привязки фото отчёта смены к листу ПСД и замечанию (ПР-03). */
export const SitePhotoSheetOption = z.strictObject({
  id: z.uuid(),
  label: z.string().min(1).max(300),
  setCode: z.string().min(1).max(120),
  number: z.string().min(1).max(80),
});
export type SitePhotoSheetOption = z.infer<typeof SitePhotoSheetOption>;

export const SitePhotoRemarkOption = z.strictObject({
  id: z.uuid(),
  label: z.string().min(1).max(400),
  number: z.string().min(1).max(40),
  status: z.string().min(1).max(40),
});
export type SitePhotoRemarkOption = z.infer<typeof SitePhotoRemarkOption>;

export const SitePhotoLinks = z.strictObject({
  projectId: z.uuid(),
  sheets: z.array(SitePhotoSheetOption).max(500),
  remarks: z.array(SitePhotoRemarkOption).max(200),
});
export type SitePhotoLinks = z.infer<typeof SitePhotoLinks>;

/** Фото и видео площадки по работе графика, листу ПСД или замечанию (ПР-03): видно из этих карточек. */
export const SitePhotoListQuery = z
  .strictObject({
    activityId: z.uuid().optional(),
    sheetId: z.uuid().optional(),
    remarkId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .refine((q) => [q.activityId, q.sheetId, q.remarkId].filter(Boolean).length <= 1, {
    message: 'Выберите одну привязку: работу, лист или замечание',
  });
export type SitePhotoListQuery = z.infer<typeof SitePhotoListQuery>;

export const SitePhotoListItem = z.strictObject({
  id: z.uuid(),
  reportId: z.uuid(),
  clientPhotoId: z.uuid(),
  reportDay: z.string(),
  reportState: z.string(),
  authorName: z.string(),
  activityId: z.uuid(),
  activityName: z.string(),
  sheetId: z.uuid().nullable(),
  sheetLabel: z.string().nullable(),
  remarkId: z.uuid().nullable(),
  remarkNumber: z.string().nullable(),
  kind: z.enum(['photo', 'video']),
  source: z.enum(['camera', 'gallery']),
  caption: z.string(),
  capturedAt: z.string().nullable(),
  /** Место сохранено с согласия автора; сами координаты в списке не отдаются (МП-07). */
  locationSaved: z.boolean(),
  /** Файл загружен и его можно открыть (`GET /site/reports/:reportId/photos/:clientPhotoId/file`). */
  fileReady: z.boolean(),
});
export type SitePhotoListItem = z.infer<typeof SitePhotoListItem>;

export const SitePhotoList = z.strictObject({
  projectId: z.uuid(),
  items: z.array(SitePhotoListItem),
});
export type SitePhotoList = z.infer<typeof SitePhotoList>;

/** Поиск уже загруженного своего файла по содержимому: повторная отправка не создаёт второй файл. */
export const SitePhotoFileLookupQuery = z.strictObject({
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  size: z.coerce.number().int().positive().max(Number.MAX_SAFE_INTEGER),
});
export const SitePhotoFileLookup = z.strictObject({ fileId: z.uuid().nullable() });
export type SitePhotoFileLookup = z.infer<typeof SitePhotoFileLookup>;
